'use client';

import { useMemo } from 'react';

import { DEFAULT_DATE_OF_BIRTH } from '@/constants';
import { useQueryConfigs } from '@/query/hooks/use-query-configs';

import type { CustomerInfoSchema } from '../profile/_schema/customer-info.schema';
import { useQueryCustomerMember } from './use-query-customer-member';

type MandatoryFieldName = keyof Pick<
  CustomerInfoSchema,
  | 'fullName'
  | 'gender'
  | 'dateOfBirth'
  | 'email'
  | 'addressStreet1'
  | 'addressStreet2'
  | 'addressCity'
  | 'addressState'
  | 'addressCountry'
  | 'addressPostalCode'
>;

const FIELD_MAP: Array<{ configKey: keyof Config; fieldName: MandatoryFieldName }> = [
  { configKey: 'requiredFullName', fieldName: 'fullName' },
  { configKey: 'requiredGender', fieldName: 'gender' },
  { configKey: 'requiredDateOfBirth', fieldName: 'dateOfBirth' },
  { configKey: 'requiredEmail', fieldName: 'email' },
  { configKey: 'requiredAddressStreet1', fieldName: 'addressStreet1' },
  { configKey: 'requiredAddressStreet2', fieldName: 'addressStreet2' },
  { configKey: 'requiredAddressCity', fieldName: 'addressCity' },
  { configKey: 'requiredAddressState', fieldName: 'addressState' },
  { configKey: 'requiredAddressCountry', fieldName: 'addressCountry' },
  { configKey: 'requiredAddressPostalCode', fieldName: 'addressPostalCode' }
];

function isFieldEmpty(fieldName: MandatoryFieldName, customer: MarketingCustomer): boolean {
  const value = customer[fieldName as keyof MarketingCustomer] as string | undefined | null;
  if (!value || value.trim() === '') return true;
  if (fieldName === 'dateOfBirth' && value === DEFAULT_DATE_OF_BIRTH) return true;
  return false;
}

export const useMandatoryFields = (customerId?: string) => {
  const { data: configs, isFetching: isConfigFetching, isLoading: isConfigLoading } = useQueryConfigs();
  const { data: customer, isFetching: isCustomerFetching, isLoading: isCustomerLoading } = useQueryCustomerMember(customerId);

  const mandatoryFieldNames = useMemo<MandatoryFieldName[]>(() => {
    if (!configs || !customer) return [];
    return FIELD_MAP.filter(({ configKey, fieldName }) => {
      return configs[configKey] === true && isFieldEmpty(fieldName, customer);
    }).map(({ fieldName }) => fieldName);
  }, [configs, customer]);

  const mandatoryFieldNamesForForm = useMemo<MandatoryFieldName[]>(() => {
    if (!configs || !customer) return [];
    return FIELD_MAP.filter(({ configKey }) => {
      return configs[configKey] === true;
    }).map(({ fieldName }) => fieldName);
  }, [configs, customer]);

  return {
    mandatoryFieldNames,
    mandatoryFieldNamesForForm,
    hasMandatoryFields: mandatoryFieldNames.length > 0,
    isFetching: isConfigFetching || isCustomerFetching,
    isLoading: isConfigLoading || isCustomerLoading
  };
};
